import React, { useState, useEffect, useContext } from "react";
import { LGrid, LButton } from "@loffa/gpp-modules";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {faPlus,faTrash,faEdit} from "@fortawesome/free-solid-svg-icons";
import styles from "./AutoImportConfigurations.module.css";
import { DeleteConfirmation } from "../../TemplateManagement/TemplateManagementModals/SharedModals/DeleteConfirmationModal";
import { AuthContext } from "../../../../../../context/AuthContext";
import { AutoImportFormData, AutoImportFormProvider } from "./Context/AutoImportFormContext";
import { AddEditAutoImportConfigurationsModal } from "./AddEditAutoImportModal/AddEditAutoImportConfigurationsModal";

export interface AutoImportConfigurationData extends AutoImportFormData {
  id: string | number;
}
interface GridCellProps {
  dataItem: AutoImportConfigurationData;
}
interface Column {
  field: string;
  title: string;
  width: number;
  cell?: (props: GridCellProps) => React.ReactNode;
  filterable?: boolean;
}

function AutoImportConfigurations() {
  const [data, setData] = useState<AutoImportConfigurationData[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<AutoImportConfigurationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<string | number | null>(null);
  const authContext = useContext(AuthContext);
  const token: string = authContext.authState().accessToken.accessToken;

  const fetchData = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_GATEWAY_BASE}/multitenancy/api/AutoImportConfigurations`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch auto import configurations.");
      const result: AutoImportConfigurationData[] = await response.json();
      setData(result);
      setError(null);
    } catch (error: any) {
      setError("Error loading data: " + error.message);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleDeleteClick = (id: string | number) => {
    setItemToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_GATEWAY_BASE}/multitenancy/api/AutoImportConfigurations/${itemToDelete}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok)
        throw new Error("Failed to delete auto import configuration.");
      setData((prevData) =>
        prevData.filter((item) => item.id !== itemToDelete)
      );
      setError(null);
    } catch (error: any) {
      setError("Error deleting configuration: " + error.message);
    } finally {
      setItemToDelete(null);
      setIsDeleteModalOpen(false);
    }
  };

  const handleAddButtonClick = () => {
    setSelectedItem(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    fetchData();
  };

  const handleSave = () => {
    setIsModalOpen(false);
    fetchData();
  };

  const handleEditClick = (item: AutoImportConfigurationData) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const columns: Column[] = [
    {
      field: "actions",
      title: "Actions",
      width: 120,
      cell: (props: GridCellProps) => (
        <td className={styles.actionButtons}>
          <button
            className={styles.editButton}
            onClick={() => handleEditClick(props.dataItem)}
            title="Edit"
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
          <button
            className={styles.deleteButton}
            onClick={() => handleDeleteClick(props.dataItem.id)}
            title="Delete"
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </td>
      ),
      filterable: false,
    },
    {
      field: "generalInformation.description",
      title: "Description",
      width: 200,
      cell: (props: GridCellProps) => (
        <td>{props.dataItem.generalInformation?.description || "-"}</td>
      ),
    },
    {
      field: "generalInformation.industryIdentifier",
      title: "Industry Identifier",
      width: 150,
      cell: (props: GridCellProps) => (
        <td>{props.dataItem.generalInformation?.industryIdentifier || "-"}</td>
      ),
    },
    {
      field: "generalInformation.autoImportType",
      title: "Auto Import Type",
      width: 150,
      cell: (props: GridCellProps) => (
        <td>{props.dataItem.generalInformation?.autoImportType?.name || "-"}</td>
      ),
    },
    {
      field: "sourceFile.connection",
      title: "Connection",
      width: 150,
      cell: (props: GridCellProps) => (
        <td>{props.dataItem.sourceFile?.connection?.description || "-"}</td>
      ),
    },
    {
      field: "sourceFile.additionalPath",
      title: "Additional Path",
      width: 200,
      cell: (props: GridCellProps) => (
        <td>{props.dataItem.sourceFile?.additionalPath || "-"}</td>
      ),
    },
    {
      field: "sourceFile.fileSelectorMode",
      title: "Selector Mode",
      width: 150,
      cell: (props: GridCellProps) => (
        <td>
          {props.dataItem.sourceFile?.fileSelectorMode ? 
            props.dataItem.sourceFile.fileSelectorMode.value === 1 ? "File Name" :
            props.dataItem.sourceFile.fileSelectorMode.value === 2 ? "Regular Expression" :
            props.dataItem.sourceFile.fileSelectorMode.value === 3 ? "Wild Card" : "-"
            : "-"}
        </td>
      ),
    },
    {
      field: "sourceFile.fileSelector",
      title: "File Selector",
      width: 150,
      cell: (props: GridCellProps) => (
        <td>{props.dataItem.sourceFile?.fileSelector || "-"}</td>
      ),
    },
    {
      field: "outputDestinations",
      title: "Output Destinations",
      width: 150,
      cell: (props: GridCellProps) => (
        <td>{props.dataItem.outputDestinations?.length || 0}</td>
      ),
    },
    {
      field: "notifications",
      title: "Notifications",
      width: 150,
      cell: (props: GridCellProps) => (
        <td>{props.dataItem.notifications?.length || 0}</td>
      ),
    },
  ];

  return (
    <div className={styles.AutoImportConfigurationsContainer}>
      <div className={styles.header}>
        <h2>Auto Import Configurations</h2>
        <LButton
          onClick={handleAddButtonClick}
          themeColor="primary"
          className="green"
        >
          <FontAwesomeIcon icon={faPlus} className={styles.buttonIcon} />
          Add Configuration
        </LButton>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <LGrid
        data={data}
        columns={columns}
        sortable
        filterable
        resizable
        pageable
        style={{ height: "400px" }}
        className={styles.grid}
      />
      <AutoImportFormProvider>
        <AddEditAutoImportConfigurationsModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSave={handleSave}
          initialData={selectedItem}
        />
      </AutoImportFormProvider>

      <DeleteConfirmation
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this configuration? This action cannot be undone."
      />
    </div>
  );
}

export { AutoImportConfigurations };